import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilter } from "@/partner/shared/components/Filter";
import { SimpleTableCard } from "@/partner/shared/components/SimpleTableCard";
import { formatNumber } from "@/shared/utils/formatNumber";
import { formatCurrency } from "@/partner/features/products";
import { useAnalyticsSalesByUtmQuery } from "@/partner/entities/analytics";
import type { UtmDimensionValue } from "./AnalyticsViewsByUtmTable";
import { DownloadReportButton } from "@/partner/features/reports";
import {
  AnalyticsWidgetType,
  ReportTypeType,
} from "@/types/graphql/partner.generated";

interface AnalyticsSalesByUtmTableProps {
  dimension: UtmDimensionValue;
  title: string;
}

export const AnalyticsSalesByUtmTable: FC<AnalyticsSalesByUtmTableProps> = ({
  dimension,
  title,
}) => {
  const { t } = useTranslation();
  const { filter } = useFilter();
  const [limit, setLimit] = useState(10);

  const { data, loading, error } = useAnalyticsSalesByUtmQuery({
    variables: { filter: filter as never, dimension: dimension as never },
  });

  const rows = (data?.analyticsSales.salesByUtm ?? [])
    .slice(0, limit)
    .map((row) => [
      row.value,
      formatNumber(row.sales),
      formatCurrency(row.revenue, data?.analyticsSales.currency ?? "USD"),
    ]);

  return (
    <SimpleTableCard
      title={title}
      columns={[
        { name: t("analytics.sales.columnUtmValue") },
        { name: t("analytics.sales.columnSales") },
        { name: t("analytics.sales.columnRevenue") },
      ]}
      rows={rows}
      loading={loading}
      error={error}
      limit={limit}
      setLimit={setLimit}
      downloadButton={
        <DownloadReportButton
          variant="card"
          type={ReportTypeType.AnalyticsWidgetType}
          widget={AnalyticsWidgetType.SalesByUtmType}
          filter={filter}
          variables={{ dimension }}
        />
      }
    />
  );
};
